import { CopyButton, ProblemNotice, SecretInput } from "@quovy/ui";
import { Button } from "@quovy/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@quovy/ui/dialog";
import { Notice } from "@quovy/ui/notice";
import { useEffect, useState } from "react";

import type { AgentTokenResult } from "../lib/api";

type EnvironmentRef = { id: string; slug: string };

export function AgentTokenDialog({
  open,
  environment,
  mode,
  onOpenChange,
  onMint,
}: {
  open: boolean;
  environment: EnvironmentRef;
  mode: "initial" | "replace";
  onOpenChange: (open: boolean) => void;
  onMint: (environment: EnvironmentRef) => Promise<AgentTokenResult>;
}) {
  const [token, setToken] = useState<AgentTokenResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setToken(null);
    setBusy(false);
    setError(null);
    setCopyError(null);
  }, [environment.id, open]);

  const mint = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      setToken(await onMint(environment));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  };

  const changeOpen = (next: boolean) => {
    if (busy) return;
    if (!next) {
      setToken(null);
      setError(null);
      setCopyError(null);
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent aria-describedby={undefined} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {token
              ? "Store this token now"
              : `${mode === "replace" ? "Replace" : "Issue"} Release Operator token`}
          </DialogTitle>
        </DialogHeader>

        {token ? (
          <div className="grid gap-4">
            {/* deliberate-ui-prose: one-time visibility is a security constraint */}
            <Notice tone="neutral">Shown once. Store this token securely.</Notice>

            <div className="grid gap-2">
              <label htmlFor="release-operator-token" className="text-sm font-medium">
                Release Operator token
              </label>
              <div className="flex items-start gap-2">
                <SecretInput
                  id="release-operator-token"
                  value={token.token}
                  readOnly
                  revealLabel="Release Operator token"
                  aria-label="Release Operator token"
                  containerClassName="min-w-0 flex-1"
                  className="font-mono"
                />
                <CopyButton
                  content={token.token}
                  copyText="Copy token"
                  copiedText="Copied"
                  onCopyError={(cause) => setCopyError(cause.message)}
                />
              </div>
            </div>

            {copyError ? (
              <ProblemNotice
                title="Token was not copied"
                howToResolve="Reveal the token and copy it from the field, then save it in a protected file."
                resolution={{ kind: "in-place" }}
                live
              >
                The browser could not write the token to the clipboard: {copyError}
              </ProblemNotice>
            ) : null}

            {/* deliberate-ui-prose: the generated token needs an explicit destination */}
            <Notice tone="info" title="Next step">
              Save the token securely, then install the Release Operator.
            </Notice>
          </div>
        ) : (
          <div className="grid gap-4">
            {mode === "replace" ? (
              <ProblemNotice
                tone="warning"
                title="Issuing a token replaces any previous credential"
                howToResolve="Issue the replacement only when you can update the Release Operator secret immediately."
                resolution={{ kind: "in-place" }}
              >
                The current token for {environment.slug} stops working as soon as this one is
                issued.
              </ProblemNotice>
            ) : null}

            {error ? (
              <ProblemNotice
                title="Release Operator token was not issued"
                howToResolve="Confirm you are signed in to this organization, then retry."
                resolution={{ kind: "in-place" }}
                live
              >
                The portal refused the credential request for {environment.slug}: {error}
              </ProblemNotice>
            ) : null}
          </div>
        )}

        <DialogFooter>
          {token ? (
            <Button onClick={() => changeOpen(false)}>I stored the token</Button>
          ) : (
            <>
              <Button variant="ghost" disabled={busy} onClick={() => changeOpen(false)}>
                Cancel
              </Button>
              <Button
                variant={mode === "replace" ? "destructive" : "default"}
                disabled={busy}
                onClick={() => void mint()}
              >
                {busy ? "Issuing…" : mode === "replace" ? "Replace token" : "Issue token"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
